import React, { useEffect, useState } from 'react';
import { fetchProfile, findOffMarketProperty } from 'bundles/connect/helpers/prospect_api';
import { Col, Nav, Row, Tab } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase, faBuilding, faBuildingMagnifyingGlass, faBuildingUser, faCabinetFiling, faCubes,
  faUsers
} from '@fortawesome/pro-solid-svg-icons';
import EmptyState from '../empty_state';
import PropertyTenants from './property_tenants';
import RecordsSection from './records_section';
import DemographicsSection from './demographics_section';
import CompsSection from './comps_section';
import ConnectPremiumCard from './connect_premium_card';
import PropertyMapView from './property_map_view';
import EndTrial from '../profile/end_trial';
import { trackEvent } from '../../components/search/helpers/google_analytics_helpers';
import Ownership from './ownership';
import FakeContacts from './owners/fake_contacts';
import Owners from './owners/index';
import PropertyLoading from './property_loading';
import PropertyHeader from './property_header';
import PropertyInfoSection from './property_info_section';
import PropertyLocationMap from './property_location_map';
import type { Profile } from 'bundles/connect/types';

type Props = {
  fromConsole: boolean;
  fromTurbo?: boolean;
  fullscreen: boolean;
  hasProfile: boolean;
  inRethink: boolean;
  insightsPropertyId: string;
}

const TAB_GA_TITLE_MAP = {
  'contacts': 'ownership',
  'tenants': 'tenants',
  'records': 'records',
  'demographics': 'demographics',
  'comps': 'comps',
};

const FAKE_LOAN_DATA = [
  {
    amount: 23000,
    lender: 'The National Bank',
    term: 360,
    startDate: '2021-07-01',
    endDate: '2023-07-01'
  },
  {
    amount: 36000,
    lender: 'The National Bank',
    term: 100,
    startDate: '2022-07-01',
    endDate: '2023-09-01'
  }
];

export default function PropertyShow(
  {
    fullscreen,
    fromConsole,
    fromTurbo = false,
    hasProfile,
    inRethink,
    insightsPropertyId,
  }: Props
) {
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState<any>();
  const [activeTab, setActiveTab] = useState('info');
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [profile, setProfile] = useState<Profile>();

  const handleTabSwitch = (eventKey) => {
    if (eventKey !== 'info' && !property.premiumAccess) {
      setShowPremiumOverlay(true);
    } else {
      setShowPremiumOverlay(false);
    }

    if (TAB_GA_TITLE_MAP[eventKey]) {
      trackEvent(`connect_off_market_${TAB_GA_TITLE_MAP[eventKey]}_tab_opened`);
    }

    setActiveTab(eventKey);
  };

  useEffect(() => {
    setLoading(true);

    const profilePromise = hasProfile ? fetchProfile() : Promise.resolve(undefined);
    const propertyPromise = findOffMarketProperty(insightsPropertyId, 'property_show');

    Promise.all([profilePromise, propertyPromise])
      .then(([profileRes, propertyRes]) => {
        if (hasProfile) {
          setProfile(profileRes);
        }
        setProperty(propertyRes);
      })
      .finally(() => {
        setLoading(false);
      });

  }, [insightsPropertyId, hasProfile]);

  if (loading) {
    return (
      <PropertyLoading showHeader={!fromConsole}/>
    );
  }

  if (!property) {
    return (
      <div className={`d-flex flex-column justify-content-center ${fromConsole ? '' : 'h-100'}`}>
        <Row className="justify-content-center">
          <Col xl={8}>
            <EmptyState
              icon={faBuilding}
              text="Our records have recently been updated and we cannot find the replacement for this property. We apologize for the inconvenience."
              title="Sorry, we couldn't find this property."
            />
          </Col>
        </Row>
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="h-100 overflow-y-auto overflow-x-hidden bg-white">
        <PropertyHeader
          fullscreen={fullscreen}
          hasProfile={false}
          inRethink={false}
          products={{ prospecting: false }}
          property={property}
        />
        <Row className={`m-0 p-6 gap-6 ${fullscreen ? 'rounded-3 overflow-hidden' : ''}`}>
          <Col>
            <PropertyInfoSection
              fromConsole={fromConsole}
              hasProfile={hasProfile}
              products={{ prospecting: false }}
              property={property}
            />
          </Col>
          <Col className="d-none d-md-block">
            <img
              className="w-100 mb-2 object-fit-cover"
              src={property.streetviewUrl}
              onError={() => this.style.display = 'none'}
            />
            <PropertyLocationMap property={property} />
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div
      className={`h-100 overflow-y-auto overflow-x-hidden bg-white ${fromConsole ? 'border rounded' : ''}`}
    >
      {!fromConsole && (
        <PropertyHeader
          fullscreen={fullscreen}
          hasProfile={hasProfile}
          inRethink={inRethink}
          products={profile?.products || { prospecting: false, exclusiveListings: false }}
          property={property}
        />
      )}
      <Tab.Container activeKey={activeTab} onSelect={handleTabSwitch}>
        {hasProfile && (
          <div className="position-sticky top-0 bg-white z-3 border-bottom">
            <div className={`overflow-x-auto py-4 ${fullscreen ? 'container-lg' : 'ps-4'}`}>
              <Nav
                className="nav-accent flex-nowrap"
                id="pills-tab"
                role="tablist"
                variant="pills"
              >
                <Nav.Item className="text-nowrap" role="presentation">
                  <Nav.Link
                    aria-controls="pills-info"
                    className="px-3 py-2"
                    eventKey="info"
                    role="tab"
                  >
                    <FontAwesomeIcon className="me-2" icon={faCubes} />
                    Summary
                  </Nav.Link>
                </Nav.Item>
                {!fromConsole && (
                  <Nav.Item className="text-nowrap" role="presentation">
                    <Nav.Link
                      aria-controls="pills-contacts"
                      className="px-3 py-2"
                      eventKey="contacts"
                      role="tab"
                    >
                      <FontAwesomeIcon className="me-2" icon={faBuildingUser} />
                      Ownership
                    </Nav.Link>
                  </Nav.Item>
                )}
                {property.tenants && property.tenants.length > 0 && property.propertyTypeId !== 10 && (
                  <Nav.Item className="text-nowrap" role="presentation">
                    <Nav.Link
                      aria-controls="pills-tenants"
                      className="px-3 py-2"
                      eventKey="tenants"
                      role="tab"
                    >
                      <FontAwesomeIcon className="me-2" icon={faBriefcase} />
                      Tenants ({property.tenants.length})
                    </Nav.Link>
                  </Nav.Item>
                )}
                <Nav.Item className="text-nowrap" role="presentation">
                  <Nav.Link
                    aria-controls="pills-records"
                    className="px-3 py-2"
                    eventKey="records"
                    role="tab"
                  >
                    <FontAwesomeIcon className="me-2" icon={faCabinetFiling} />
                    Records {property.propertyHistory && property.propertyHistory.length > 0 ? `(${property.propertyHistory.length})` : ''}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap" role="presentation">
                  <Nav.Link
                    aria-controls="pills-comps"
                    className="px-3 py-2"
                    eventKey="comps"
                    role="tab"
                  >
                    <FontAwesomeIcon className="me-2" icon={faBuildingMagnifyingGlass} />
                    Comps
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap" role="presentation">
                  <Nav.Link
                    aria-controls="pills-demographics"
                    className="px-3 py-2"
                    eventKey="demographics"
                    role="tab"
                  >
                    <FontAwesomeIcon className="me-2" icon={faUsers} />
                    Demographics
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          </div>
        )}

        <div className={fullscreen ? 'container-lg pt-md-4 pt-0' : ''}>
          <div className="position-relative">
            <div className="position-relative d-none d-md-block">
              <PropertyMapView fullscreen={fullscreen} property={property} />
            </div>
            { profile?.trialUser && (
              <div className="px-4 py-3">
                <EndTrial renewalDate={profile?.subscriptionRenewalDate} />
              </div>
            )}
            <div className="px-4 py-3 overflow-y-auto">
              <Tab.Content>
                <Tab.Pane eventKey="info">
                  <PropertyInfoSection
                    fromConsole={fromConsole}
                    hasProfile={hasProfile}
                    products={profile?.products || { prospecting: false, exclusiveListings: false }}
                    property={property}
                  />
                </Tab.Pane>
                {!fromConsole && (
                  <Tab.Pane eventKey="contacts">
                    {property.premiumAccess && (
                      <div className="mb-6">
                        <Ownership
                          faOwners={property.owners}
                          insightsPropertyId={insightsPropertyId}
                          ownerAddress={property.ownerAddress}
                          ownerOccupied={property.ownerOccupied}
                        />
                      </div>
                    )}
                    {!fromConsole && (
                      property.premiumAccess ? (
                        <Owners
                          insightsPropertyId={insightsPropertyId}
                          showOnClick={activeTab === 'contacts'}
                        />
                      ) : (
                        <div className="d-none d-md-block">
                          <FakeContacts />
                        </div>
                      )
                    )}
                  </Tab.Pane>
                )}
                {property.tenants && property.tenants.length > 0 && property.propertyTypeId != 10 && (
                  <Tab.Pane eventKey="tenants">
                    <PropertyTenants tenants={property.tenants} />
                  </Tab.Pane>
                )}
                <Tab.Pane eventKey="records">
                  <RecordsSection
                    loans={property.premiumAccess ? property.loans : FAKE_LOAN_DATA}
                    records={property.propertyHistory || []}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="demographics">
                  <DemographicsSection
                    insightsPropertyId={insightsPropertyId}
                    latitude={property.latitude}
                    longitude={property.longitude}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="comps">
                  <CompsSection fromTurbo={fromTurbo} hasAccess={property.premiumAccess} subjectProperty={property} />
                </Tab.Pane>
              </Tab.Content>
            </div>
            {showPremiumOverlay && (
              <>
                <ConnectPremiumCard
                  fromConsole={fromConsole}
                  hasActiveConnectProduct={profile?.products.prospecting || false}
                />
                <div
                  className="d-md-block position-absolute top-0 left-0 w-100 h-100"
                  style={{ backdropFilter: 'blur(2.5px)' }}
                />
              </>
            )}
          </div>
        </div>
      </Tab.Container>
    </div>
  );
}
